// src/pages/Admin.tsx
import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";

export default function Admin() {
  const [password, setPassword] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [loadingImages, setLoadingImages] = useState<string | null>(null);
  const [images, setImages] = useState<Record<string, Record<string, string>>>({});

  const handleLogin = async () => {
    const res = await fetch("/api/kyc/admin/list", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ adminPassword: password }),
    });
    if (res.ok) {
      const data = await res.json();
      setRequests(data.requests);
      setAuthenticated(true);
    } else {
      toast({ title: "Error", description: "Invalid admin password", variant: "destructive" });
    }
  };

  const handleAction = async (email: string, action: "approve" | "reject") => {
    const res = await fetch("/api/kyc/admin/action", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ adminPassword: password, email, action }),
    });
    if (res.ok) {
      toast({ title: "Success", description: `KYC ${action}d for ${email}` });
      setRequests(prev => prev.filter(r => r.email !== email));
    } else {
      toast({ title: "Error", description: "Action failed", variant: "destructive" });
    }
  };

  const handleLoadImages = async (tempKey: string, email: string) => {
    if (images[tempKey]) {
      setExpandedRow(expandedRow === tempKey ? null : tempKey);
      return;
    }

    setLoadingImages(tempKey);
    try {
      const imageTypes = ["idFront", "idBack", "liveImage"];
      const imageData: Record<string, string> = {};

      await Promise.all(
        imageTypes.map(async (type) => {
          const res = await fetch("/api/kyc/admin/image", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ adminPassword: password, tempKey, imageType: type }),
          });
          if (res.ok) {
            const data = await res.json();
            imageData[type] = data.imageData;
          }
        })
      );

      setImages(prev => ({ ...prev, [tempKey]: imageData }));
      setExpandedRow(tempKey);
    } catch (err) {
      toast({ title: "Error", description: "Failed to load images", variant: "destructive" });
    } finally {
      setLoadingImages(null);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-card p-6 rounded-lg shadow-lg max-w-sm w-full">
          <h1 className="text-xl font-bold mb-4">Admin Login</h1>
          <Input type="password" placeholder="Admin Password" value={password} onChange={e => setPassword(e.target.value)} />
          <Button onClick={handleLogin} className="mt-4 w-full">Login</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <h1 className="text-2xl font-bold mb-6">KYC Verification Requests</h1>
      {requests.length === 0 ? (
        <p>No pending KYC requests.</p>
      ) : (
        <div className="space-y-4">
          {requests.map((req) => (
            <div key={req.tempKey} className="bg-card rounded-lg border">
              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div><strong>Email:</strong> {req.email}</div>
                    <div><strong>Phone:</strong> {req.phone}</div>
                    <div><strong>Submitted:</strong> {new Date(req.submittedAt).toLocaleString()}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleLoadImages(req.tempKey, req.email)}
                      variant="outline"
                      disabled={loadingImages === req.tempKey}
                    >
                      {loadingImages === req.tempKey ? "Loading..." : expandedRow === req.tempKey ? "Hide" : "View"} Images
                    </Button>
                  </div>
                </div>
              </div>

              {expandedRow === req.tempKey && images[req.tempKey] && (
                <div className="border-t p-4 bg-muted/50">
                  <div className="grid grid-cols-3 gap-4">
                    {["idFront", "idBack", "liveImage"].map((type) => (
                      <div key={type}>
                        <p className="text-sm font-semibold mb-2">{type === "idFront" ? "ID Front" : type === "idBack" ? "ID Back" : "Live Selfie"}</p>
                        <img
                          src={images[req.tempKey][type]}
                          alt={type}
                          className="w-full h-auto border rounded max-h-64 object-cover"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2 mt-6">
                    <Button
                      onClick={() => handleAction(req.email, "approve")}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Approve
                    </Button>
                    <Button
                      onClick={() => handleAction(req.email, "reject")}
                      variant="destructive"
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
